
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { LoadingState } from "@/components/ui/loading-states";

interface AuthGuardProps {
  children: React.ReactNode;
  requireSubscription?: boolean;
}

export const AuthGuard = ({ children, requireSubscription = false }: AuthGuardProps) => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [hasAccess, setHasAccess] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const checkAuthAndAccess = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        
        if (!session) {
          console.log('No session found, redirecting to auth');
          navigate('/auth', { replace: true });
          return;
        }

        setIsAuthenticated(true);

        if (requireSubscription) {
          // Check for active subscription or trial
          const [subscriptionResult, trialResult] = await Promise.all([
            supabase
              .from('user_subscriptions')
              .select('*')
              .eq('user_id', session.user.id)
              .eq('status', 'active')
              .maybeSingle(),
            supabase
              .from('free_trials')
              .select('*')
              .eq('user_id', session.user.id)
              .eq('status', 'active')
              .maybeSingle()
          ]);

          const hasSubscription = !!subscriptionResult.data;
          const hasTrial = !!trialResult.data;

          if (!hasSubscription && !hasTrial) {
            console.log('No active subscription or trial, redirecting to plan selection');
            navigate('/plan-selection', { replace: true });
            return;
          }
        }

        setHasAccess(true);
      } catch (error) {
        console.error('Error in AuthGuard:', error);
        navigate('/auth', { replace: true });
      } finally {
        setIsLoading(false);
      }
    };

    checkAuthAndAccess();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (event === 'SIGNED_OUT' || !session) {
          navigate('/auth', { replace: true });
        } else if (event === 'SIGNED_IN') {
          // Re-check access when user signs in
          checkAuthAndAccess();
        }
      }
    );

    return () => subscription.unsubscribe();
  }, [navigate, requireSubscription]);

  if (isLoading) {
    return <LoadingState message="Checking access..." />;
  }

  if (!isAuthenticated || (requireSubscription && !hasAccess)) {
    return <LoadingState message="Redirecting..." />;
  }

  return <>{children}</>;
};
